import { valueIsDefined } from "@legacy/core/utils/utils"
import clsx from "clsx"
import { useMemo, useState } from "react"

import { faIcon } from "@utils/faIcon"

import SkeletonTypography from "@particles/SkeletonTypography/SkeletonTypography"
import TruncatableText from "@particles/TruncatableText/TruncatableText"

import { LinkButton } from "@atoms"

import styles from "@molecules/ValueDisplay/ValueDisplay.module.scss"
import type { ValueDisplayVerticalTextProps } from "@molecules/ValueDisplay/ValueDisplay.types"

// The design's standard line limit for long text that opts into clamping.
export const DEFAULT_MAX_LINES = 4

export default function VerticalTextValueDisplay(props: ValueDisplayVerticalTextProps) {
    const { className, label, showLabel = true, value, maxLines, isLoading = false } = props

    const [isExpanded, setIsExpanded] = useState(false)
    const [isClamped, setIsClamped] = useState(false)

    const hasValue = valueIsDefined(value)
    const placeholder = `No ${label}`

    // The Show more/less toggle is only available when a line limit is set.
    const canToggle = !!maxLines

    const chevronIcon = useMemo(
        () =>
            isExpanded
                ? faIcon({ name: "angle-up", style: "regular" })
                : faIcon({ name: "angle-down", style: "regular" }),
        [isExpanded],
    )

    return (
        <div className={clsx(styles.base, styles.vertical, className)}>
            {showLabel && <div className={styles.label}>{label}</div>}

            {isLoading ? (
                <div className={styles.verticalLoadingSkeleton}>
                    <SkeletonTypography variant="bodySpacious" />
                    <SkeletonTypography variant="bodySpacious" className={styles.skeletonLine90} />
                    <SkeletonTypography variant="bodySpacious" className={styles.skeletonLine95} />
                    <SkeletonTypography variant="bodySpacious" className={styles.skeletonLine70} />
                </div>
            ) : (
                <>
                    {/* An expanded value has nothing to clamp, so the last collapsed measurement stands. */}
                    {canToggle && !isExpanded ? (
                        <TruncatableText
                            text={hasValue ? value : placeholder}
                            lines={maxLines}
                            onTruncationChange={setIsClamped}
                            className={clsx(styles.value, !hasValue && styles.placeholderValue)}
                        />
                    ) : (
                        <div className={clsx(styles.value, !hasValue && styles.placeholderValue)}>
                            {hasValue ? value : placeholder}
                        </div>
                    )}

                    {canToggle && hasValue && (isClamped || isExpanded) && (
                        <LinkButton
                            colorScheme="gray"
                            rightIcon={chevronIcon}
                            onClick={() => setIsExpanded((prev) => !prev)}
                            noDebounce={true}
                        >
                            {isExpanded ? "Show less" : "Show more"}
                        </LinkButton>
                    )}
                </>
            )}
        </div>
    )
}
